import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, CreditCard, Link2, Users } from "lucide-react";
import { toast } from "sonner";

import { ChalkCard, SectionTitle } from "../chalk/primitives";
import { director, tbilling, usd } from "../../lib/tournamentApi";
import { inputStyle } from "./shared";

const SOURCE = { hub_paid: "Hub · paid per event", free_first: "Hub · free first event", admin_free: "Hub · free (granted)",
                 director: "Hub · Director plan", director_plus: "Hub · Director Plus", admin: "Hub · set by ChalkOS" };
const ADJ = { none: null, scheduled: "Charge scheduled", charged: "Charged", refunded: "Refunded", failed: "Payment failed" };

/** Settings → Billing: what was paid, what's counted now, what happens after the event. */
export default function EventBillingCard({ data }) {
  const t = data.tournament;
  const [q, setQ] = useState(null);
  useEffect(() => { tbilling.event(t.id).then(setQ).catch(() => {}); }, [t.id, t.tier]);
  if (!q || data.my_role !== "director") return null;
  const b = q.billing;
  return (
    <>
      <SectionTitle label="Billing" />
      <ChalkCard data-testid="event-billing" style={{ marginBottom: 14 }}>
        {!b ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <CreditCard size={18} color="var(--green-mid)" aria-hidden="true" />
            <div style={{ flex: 1, fontSize: 13 }}>{t.tier === "hub" ? "Hub (preview)" : "Whiteboard · free"}</div>
            {t.tier !== "hub" && <Link to={`/app/tournaments/${t.id}/upgrade`} data-testid="event-billing-upgrade" style={linkStyle}>See Hub pricing</Link>}
          </div>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }} data-testid="event-billing-source">{SOURCE[b.source] || b.source}</div>
            {b.amount_paid_cents > 0 && <div>Paid {usd(b.amount_paid_cents)} ({labelFor(q, b.paid_band)})</div>}
            <div data-testid="event-billing-counted">Counted so far: <b>{b.counted_teams}</b> teams (checked in or played) · covered up to {labelFor(q, b.covered_band)}</div>
            {b.status === "active" && b.projected_adjustment_cents > 0 && (
              <div data-testid="event-billing-projected" style={{ background: "#FFF8E6", borderRadius: 8, padding: "8px 10px", marginTop: 6 }}>
                {b.counted_teams} teams is above your covered band. After the event you'll be charged {usd(b.projected_adjustment_cents)}, with 48 hours' notice. Nothing changes today.</div>
            )}
            {b.status === "active" && b.projected_adjustment_cents < 0 && (
              <div data-testid="event-billing-projected" style={{ color: "var(--chalk-muted)" }}>If the final count stays here, we'll refund {usd(b.projected_adjustment_cents)} after the event.</div>
            )}
            {b.status === "finalized" && (
              <div data-testid="event-billing-final" style={{ marginTop: 6 }}>
                Final: {b.final_team_count} teams{b.extra_helpers ? ` · ${b.extra_helpers} extra helper${b.extra_helpers > 1 ? "s" : ""}` : ""}
                {b.adjustment_cents ? ` · ${b.adjustment_cents > 0 ? "" : "refund "}${usd(b.adjustment_cents)}` : " · nothing more to pay"}
                {ADJ[b.adjustment_status] && <b> · {ADJ[b.adjustment_status]}{b.adjustment_status === "scheduled" && b.adjustment_due_at ? ` for ${new Date(b.adjustment_due_at).toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}</b>}
              </div>
            )}
          </div>
        )}
      </ChalkCard>
      {q.plan !== null && q.plan !== undefined && <DirectorToolsCard data={data} />}
    </>
  );
}

function labelFor(q, key) {
  return q.bands.find((x) => x.key === key)?.label || key;
}

function DirectorToolsCard({ data }) {
  const t = data.tournament;
  const nav = useNavigate();
  const [slug, setSlug] = useState(t.slug);
  const [name, setName] = useState(`${t.name} (copy)`);
  const [date, setDate] = useState("");
  async function run(fn, ok) { try { const r = await fn(); toast.success(ok(r)); return r; } catch (e) { toast.error(e.message); return null; } }
  return (
    <>
      <SectionTitle label="Director tools" />
      <ChalkCard data-testid="director-tools-card" style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Link2 size={15} aria-hidden="true" /><span style={{ fontSize: 12, color: "var(--chalk-muted)" }}>/t/</span>
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} data-testid="custom-slug-input" style={{ ...inputStyle, flex: 1 }} />
          <button data-testid="custom-slug-save" style={btn} onClick={() => run(() => director.setSlug(t.id, slug), () => "Custom link saved. The old link keeps working.")}>Save</button>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <Copy size={15} aria-hidden="true" />
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="copy-name" style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="copy-date" style={{ ...inputStyle, flex: 1 }} />
          <button data-testid="copy-tournament" style={btn} disabled={!date} onClick={async () => {
            const r = await run(() => director.copy(t.id, { name, starts_on: `${date}T16:00:00Z`, include_teams: false }), () => "Tournament copied.");
            if (r) nav(`/app/tournaments/${r.tournament.id}`);
          }}>Copy settings</button>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Users size={15} aria-hidden="true" />
          <span style={{ flex: 1, fontSize: 13 }}>Import teams you've used before (name + captain)</span>
          <button data-testid="import-saved-teams" style={btn} onClick={async () => {
            const s = await director.savedTeams().catch((e) => { toast.error(e.message); return null; });
            if (s) await run(() => director.importTeams(t.id, s.teams.map((x) => x.name)), (r) => `Imported ${r.added} team${r.added === 1 ? "" : "s"}.`);
          }}>Import all</button>
        </div>
      </ChalkCard>
    </>
  );
}

const linkStyle = { fontSize: 12, fontWeight: 700, color: "var(--green-mid)" };
const btn = { background: "transparent", border: "0.5px solid var(--chalk-border)", borderRadius: 8, padding: "7px 12px", color: "var(--green-mid)", fontWeight: 700, fontSize: 12, cursor: "pointer" };
